import type {
  FetchBatchCfarOffers,
  FetchBatchCfarOffersRequest,
} from "@b2bportal/core-flights-cfar-offers";
import type {
  HtsAirCfarOffer,
  SyntheticHtsAirCfarOffer,
} from "@b2bportal/core-types";
import type { AccountPrice } from "@b2bportal/core-types";
import {
  rbcCfarApi,
  type BatchCreateOffersRequest,
  type CfarOffer,
} from "@b2bportal/rbc-cfar-api";
import axios from "axios";

/**
 * Extends the auto-generated CfarOffer type to include the `rewards` field
 * that the API returns in `feeAmountPerPassenger` but is not yet in the
 * generated OpenAPI types.
 */
interface CfarOfferWithRewards extends CfarOffer {
  feeAmountPerPassenger: CfarOffer["feeAmountPerPassenger"] & {
    rewards?: Record<string, AccountPrice>;
  };
}

export const fetchBatchRbcCfarOffers: FetchBatchCfarOffers = async (
  request: FetchBatchCfarOffersRequest
) => {
  const { fareTrips, passengers, funnelId } = request;

  // funnelId is omitted when no air funnel is active so the backend falls
  // back to the authenticated session ID.
  const batchRequest: BatchCreateOffersRequest = {
    itineraries: fareTrips.map(({ tripId, fareId, farePriceBreakdown }) => ({
      tripId: { value: tripId },
      fareId: { value: fareId },
      farePriceBreakdown,
      passengers,
    })),
    ...(funnelId != null ? { funnelId } : {}),
  };

  const response =
    await rbcCfarApi(axios).apiV0CfarAirOffersBatchPost(batchRequest);

  const result: Record<string, HtsAirCfarOffer[]> = {};
  for (const { tripId, fareId } of fareTrips) {
    const offersByTripId = response.data.responseByTripId[tripId] ?? {};
    const cfarOffer = offersByTripId[fareId];
    result[fareId] = cfarOffer != null ? [toSyntheticOffer(cfarOffer)] : [];
  }
  return result;
};

const LOYALTY_PROGRAM_PREMIUM_TEMPLATE =
  "{{loyalty_program_premium_amount_per_passenger}}";

/**
 * Extracts the rewards premium value from the offer's fee amount.
 * Uses the same account ID key from `accountSpecific` to look up the
 * corresponding reward entry, ensuring deterministic selection.
 */
export function getRewardsPremiumLabel(
  offer: CfarOfferWithRewards
): string | undefined {
  const { rewards, accountSpecific } = offer.feeAmountPerPassenger;
  if (rewards == null) return undefined;

  const accountIds = Object.keys(accountSpecific);
  if (accountIds.length === 0) return undefined;

  const reward = rewards[accountIds[0]];
  if (reward == null) return undefined;

  return reward.value.toLocaleString("en-US");
}

/**
 * Replaces `{{loyalty_program_premium_amount_per_passenger}}` in merchandising
 * content values with the actual rewards premium label. When no rewards value
 * is available, keys containing the template are omitted so the dialog falls
 * back to the corresponding `_no_loyalty` variant.
 */
export function processMerchandisingTemplates(
  merchandisingContents: Record<string, string>,
  rewardsPremiumLabel: string | undefined
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(merchandisingContents)) {
    if (!value.includes(LOYALTY_PROGRAM_PREMIUM_TEMPLATE)) {
      result[key] = value;
    } else if (rewardsPremiumLabel != null) {
      result[key] = value.replaceAll(
        LOYALTY_PROGRAM_PREMIUM_TEMPLATE,
        rewardsPremiumLabel
      );
    }
    // else: omit — the dialog falls back to the _no_loyalty variant
  }
  return result;
}

function toSyntheticOffer(offer: CfarOffer): SyntheticHtsAirCfarOffer {
  // TODO: Remove cast once rbc-cfar-api OpenAPI spec includes `rewards` in Prices
  const rewardsPremiumLabel = getRewardsPremiumLabel(
    offer as CfarOfferWithRewards
  );

  return {
    id: offer.id,
    feeAmountPerPassenger: offer.feeAmountPerPassenger,
    merchandisingContents: processMerchandisingTemplates(
      offer.merchandisingContents,
      rewardsPremiumLabel
    ),
    coveragePercentage: offer.coveragePercentage,
  };
}
